import { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Filter, X, Share2 } from 'lucide-react'
import ForceGraph2D from 'react-force-graph-2d'
import { useRelationshipGraph } from '@/hooks/use-relationship-graph'
import type { GraphNode } from '@/hooks/use-relationship-graph'
import { EmptyState } from '@/components/EmptyState'
import { cn } from '@/lib/utils'

const RELATIONSHIP_COLORS: Record<string, string> = {
  friend: '#22c55e',
  family: '#a855f7',
  colleague: '#3b82f6',
  acquaintance: '#94a3b8',
  introduced_by: '#f59e0b',
  interaction: '#64748b',
}

const RELATIONSHIP_LABELS: Record<string, string> = {
  friend: 'Friend',
  family: 'Family',
  colleague: 'Colleague',
  acquaintance: 'Acquaintance',
  introduced_by: 'Introduced by',
  interaction: 'Shared interactions',
}

function getNodeLabel(node: GraphNode) {
  return node.lastName ? `${node.firstName} ${node.lastName}` : node.firstName
}

export default function NetworkPage() {
  const navigate = useNavigate()
  const { data, isLoading } = useRelationshipGraph()
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null)
  const [filterType, setFilterType] = useState<string | null>(null)
  const [showFilters, setShowFilters] = useState(false)

  useEffect(() => {
    function updateSize() {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        // Use clientHeight when available (flex parent gives height), otherwise
        // compute from viewport minus container top and bottom-nav clearance.
        const height = containerRef.current.clientHeight || window.innerHeight - rect.top - 80
        setDimensions({
          width: rect.width || containerRef.current.clientWidth,
          height: Math.max(height, 200),
        })
      }
    }
    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [])

  const graphData = useMemo(() => {
    if (!data) return { nodes: [], links: [] }

    const filteredEdges = filterType
      ? data.edges.filter((e) => e.relationshipType === filterType)
      : data.edges

    // Only include nodes that are part of filtered edges
    const nodeIds = new Set<string>()
    filteredEdges.forEach((e) => {
      nodeIds.add(e.source)
      nodeIds.add(e.target)
    })

    const filteredNodes = data.nodes.filter((n) => nodeIds.has(n.id))

    return {
      nodes: filteredNodes.map((n) => ({ ...n })),
      links: filteredEdges.map((e) => ({
        ...e,
        source: e.source,
        target: e.target,
      })),
    }
  }, [data, filterType])

  const maxWeight = useMemo(() => {
    if (!graphData.links.length) return 1
    return Math.max(...graphData.links.map((l) => l.weight ?? 1))
  }, [graphData])

  const handleNodeClick = useCallback(
    (node: { id?: string | number } & Record<string, unknown>) => {
      const graphNode = data?.nodes.find((n) => n.id === node.id) ?? null
      setSelectedNode(graphNode)
    },
    [data],
  )

  const nodeCanvasObject = useCallback(
    (
      node: { id?: string | number; x?: number; y?: number } & Record<string, unknown>,
      ctx: CanvasRenderingContext2D,
      globalScale: number,
    ) => {
      const n = node as unknown as GraphNode & { x?: number; y?: number }
      const label = getNodeLabel(n)
      const x = n.x ?? 0
      const y = n.y ?? 0
      const isSelected = selectedNode?.id === n.id
      const radius = isSelected ? 8 : 6

      // Node circle
      ctx.beginPath()
      ctx.arc(x, y, radius, 0, 2 * Math.PI)
      ctx.fillStyle = isSelected ? '#FF6F61' : '#0F2E28'
      ctx.fill()

      if (isSelected) {
        ctx.strokeStyle = '#0DE55A'
        ctx.lineWidth = 2
        ctx.stroke()
      }

      // Label
      const fontSize = Math.max(12 / globalScale, 3)
      ctx.font = `${fontSize}px -apple-system, BlinkMacSystemFont, sans-serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'
      ctx.fillStyle =
        getComputedStyle(document.documentElement).getPropertyValue('--color-on-surface').trim() ||
        '#e2e8f0'
      ctx.fillText(label, x, y + radius + 2)
    },
    [selectedNode],
  )

  const linkColor = useCallback((link: Record<string, unknown>) => {
    const type = (link.relationshipType as string) ?? 'acquaintance'
    return RELATIONSHIP_COLORS[type] ?? '#94a3b8'
  }, [])

  const linkWidth = useCallback(
    (link: Record<string, unknown>) => {
      const weight = (link.weight as number) ?? 1
      return 1 + (weight / maxWeight) * 4
    },
    [maxWeight],
  )

  const relationshipTypes = useMemo(() => {
    if (!data) return []
    const types = new Set(data.edges.map((e) => e.relationshipType))
    return Array.from(types).sort()
  }, [data])

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    )
  }

  if (!data || data.nodes.length === 0) {
    return (
      <div className="flex flex-1 flex-col px-6 pt-6">
        <div className="mb-6 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-on-surface-variant">
            <ArrowLeft className="size-5" />
          </button>
          <h1 className="font-heading text-2xl font-bold">Network</h1>
        </div>
        <EmptyState
          icon={Share2}
          title="No connections yet"
          description="Add relationships between your contacts to see your network graph."
        />
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-[60dvh] flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-6 pb-2">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-on-surface-variant">
            <ArrowLeft className="size-5" />
          </button>
          <h1 className="font-heading text-2xl font-bold">Network</h1>
          <span className="text-on-surface-variant text-sm">
            {graphData.nodes.length} contacts · {graphData.links.length} connections
          </span>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={cn(
            'rounded-lg p-2 transition-colors',
            showFilters
              ? 'bg-primary/10 text-primary'
              : 'text-on-surface-variant hover:bg-surface-container-high',
          )}
        >
          <Filter className="size-5" />
        </button>
      </div>

      {/* Filter bar */}
      {showFilters && (
        <div className="flex flex-wrap gap-2 px-6 pb-2">
          <button
            onClick={() => setFilterType(null)}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-medium transition-colors',
              !filterType
                ? 'bg-primary text-on-primary'
                : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest',
            )}
          >
            All
          </button>
          {relationshipTypes.map((type) => (
            <button
              key={type}
              onClick={() => setFilterType(type === filterType ? null : type)}
              className={cn(
                'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                filterType === type
                  ? 'text-white'
                  : 'bg-surface-container-high text-on-surface-variant hover:bg-surface-container-highest',
              )}
              style={
                filterType === type
                  ? { backgroundColor: RELATIONSHIP_COLORS[type] ?? '#94a3b8' }
                  : undefined
              }
            >
              {RELATIONSHIP_LABELS[type] ?? type}
            </button>
          ))}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-3 px-6 pb-2">
        {relationshipTypes.map((type) => (
          <div key={type} className="flex items-center gap-1.5">
            <div
              className="size-2.5 rounded-full"
              style={{ backgroundColor: RELATIONSHIP_COLORS[type] ?? '#94a3b8' }}
            />
            <span className="text-on-surface-variant text-xs">
              {RELATIONSHIP_LABELS[type] ?? type}
            </span>
          </div>
        ))}
      </div>

      {/* Graph container */}
      <div ref={containerRef} className="relative min-h-0 flex-1">
        {dimensions.width > 0 && (
          <ForceGraph2D
            graphData={graphData}
            width={dimensions.width}
            height={dimensions.height}
            nodeId="id"
            linkSource="source"
            linkTarget="target"
            nodeCanvasObject={nodeCanvasObject}
            nodePointerAreaPaint={(node, color, ctx) => {
              const x = (node as Record<string, number>).x ?? 0
              const y = (node as Record<string, number>).y ?? 0
              ctx.beginPath()
              ctx.arc(x, y, 8, 0, 2 * Math.PI)
              ctx.fillStyle = color
              ctx.fill()
            }}
            linkColor={linkColor}
            linkWidth={linkWidth}
            linkDirectionalParticles={0}
            onNodeClick={handleNodeClick}
            onBackgroundClick={() => setSelectedNode(null)}
            cooldownTicks={100}
            backgroundColor="transparent"
          />
        )}

        {/* Selected node info panel */}
        {selectedNode && (
          <div className="bg-surface-container border-outline-variant/30 absolute right-4 bottom-4 left-4 rounded-2xl border p-4 shadow-lg sm:left-auto sm:w-80">
            <div className="mb-2 flex items-start justify-between">
              <div>
                <h3 className="font-semibold">{getNodeLabel(selectedNode)}</h3>
                {selectedNode.jobTitle && (
                  <p className="text-on-surface-variant text-sm">{selectedNode.jobTitle}</p>
                )}
                {selectedNode.company && (
                  <p className="text-on-surface-variant text-sm">{selectedNode.company}</p>
                )}
              </div>
              <button onClick={() => setSelectedNode(null)} className="text-on-surface-variant">
                <X className="size-4" />
              </button>
            </div>
            {/* Show connections for selected node */}
            <div className="border-outline-variant/20 mt-2 border-t pt-2">
              <p className="text-on-surface-variant mb-1 text-xs font-medium tracking-wider uppercase">
                Connections
              </p>
              <div className="max-h-32 space-y-1 overflow-y-auto">
                {data.edges
                  .filter((e) => e.source === selectedNode.id || e.target === selectedNode.id)
                  .map((edge) => {
                    const otherId = edge.source === selectedNode.id ? edge.target : edge.source
                    const other = data.nodes.find((n) => n.id === otherId)
                    if (!other) return null
                    return (
                      <div key={edge.id} className="flex items-center justify-between text-sm">
                        <button
                          onClick={() => navigate(`/contacts/${other.id}`)}
                          className="text-primary hover:underline"
                        >
                          {getNodeLabel(other)}
                        </button>
                        <div className="flex items-center gap-2">
                          <div
                            className="size-2 rounded-full"
                            style={{
                              backgroundColor:
                                RELATIONSHIP_COLORS[edge.relationshipType] ?? '#94a3b8',
                            }}
                          />
                          <span className="text-on-surface-variant text-xs">
                            {RELATIONSHIP_LABELS[edge.relationshipType] ?? edge.relationshipType}
                          </span>
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
            <button
              onClick={() => navigate(`/contacts/${selectedNode.id}`)}
              className="text-primary mt-3 text-sm font-medium hover:underline"
            >
              View contact
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
